import axios from 'axios';
import {
  User,
  Course,
  Lesson,
  Purchase,
  Enrollment,
  AuthResponse,
  PaymentIntentResponse,
  CreateCourseData,
  CreateLessonData,
  RegisterData,
  LoginData,
  CourseProgress,
  LessonCompletion,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  register: (data: RegisterData): Promise<User> =>
    api.post('/api/auth/register', data).then(res => res.data),
  
  login: (data: LoginData): Promise<AuthResponse> =>
    api.post('/api/auth/login', data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      transformRequest: [(data) => {
        const params = new URLSearchParams();
        Object.entries(data).forEach(([key, value]) => {
          params.append(key, value as string);
        });
        return params;
      }]
    }).then(res => res.data),
  
  getMe: (): Promise<User> =>
    api.get('/api/auth/me').then(res => res.data),
};

// Courses API
export const coursesAPI = {
  getCourses: (): Promise<Course[]> => 
    api.get('/api/courses/').then(res => res.data),
  getCourse: (id: number): Promise<Course> => 
    api.get(`/api/courses/${id}`).then(res => res.data),
  enrollInCourse: (courseId: number): Promise<{ message: string }> => 
    api.post(`/api/courses/enroll/${courseId}`).then(res => res.data),
  
  // Progress and completion
  getCourseProgress: (courseId: number): Promise<CourseProgress> => 
    api.get(`/api/courses/${courseId}/progress`).then(res => res.data),
  completeLesson: (lessonId: number): Promise<LessonCompletion> => 
    api.post(`/api/courses/lessons/${lessonId}/complete`).then(res => res.data),
  
  // File access
  getFileSignedUrl: (fileId: number): Promise<{ signed_url: string; expires_in: number }> => 
    api.get(`/api/courses/files/${fileId}/signed-url`).then(res => res.data),
  
  createCourse: (data: CreateCourseData): Promise<Course> =>
    api.post('/api/courses/', data).then(res => res.data),
  
  updateCourse: (id: number, data: Partial<CreateCourseData>): Promise<Course> =>
    api.put(`/api/courses/${id}`, data).then(res => res.data),
  
  deleteCourse: (id: number): Promise<void> =>
    api.delete(`/api/courses/${id}`).then(res => res.data),
  
  getMyCourses: (): Promise<Course[]> =>
    api.get('/api/courses/my-courses').then(res => res.data),
  
  createLesson: (courseId: number, data: CreateLessonData): Promise<Lesson> =>
    api.post(`/api/courses/${courseId}/lessons`, data).then(res => res.data),
  
  updateLesson: (lessonId: number, data: Partial<CreateLessonData>): Promise<Lesson> =>
    api.put(`/api/courses/lessons/${lessonId}`, data).then(res => res.data),
  
  deleteLesson: (lessonId: number): Promise<void> =>
    api.delete(`/api/courses/lessons/${lessonId}`).then(res => res.data),
  
  uploadFile: (lessonId: number, file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/courses/lessons/${lessonId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },

  deleteFile: (fileId: number): Promise<{ message: string }> =>
    api.delete(`/api/courses/files/${fileId}`).then(res => res.data),

  updateFileOrder: (lessonId: number, files: Array<{ file_id: number; order: number }>): Promise<{ message: string }> =>
    api.put(`/api/courses/lessons/${lessonId}/files/order`, { files }).then(res => res.data),
};

// Payments API
export const paymentsAPI = {
  createPaymentIntent: (courseId: number, amount: number): Promise<PaymentIntentResponse> =>
    api.post('/api/payments/create-payment-intent', {
      course_id: courseId,
      amount,
      currency: 'usd'
    }).then(res => res.data),
  
  getPurchases: (): Promise<Purchase[]> =>
    api.get('/api/payments/purchases').then(res => res.data),
  
  getEnrollments: (): Promise<Enrollment[]> =>
    api.get('/api/payments/enrollments').then(res => res.data),
  
  enrollFreeCourse: (courseId: number): Promise<{ message: string }> =>
    api.post(`/api/payments/enroll/${courseId}`).then(res => res.data),
};

export default api;

// Admin API
export const adminAPI = {
  // Course management
  createCourse: (data: import('../types').AdminCourseCreate): Promise<import('../types').Course> =>
    api.post('/api/admin/courses', data).then(res => res.data),
  
  getAllCourses: (published_only: boolean = false): Promise<import('../types').Course[]> =>
    api.get(`/api/admin/courses?published_only=${published_only}`).then(res => res.data),
  
  updateCourse: (courseId: number, data: import('../types').AdminCourseUpdate): Promise<import('../types').Course> =>
    api.put(`/api/admin/courses/${courseId}`, data).then(res => res.data),
  
  deleteCourse: (courseId: number): Promise<{ message: string }> =>
    api.delete(`/api/admin/courses/${courseId}`).then(res => res.data),
  
  // Lesson management
  createLesson: (courseId: number, data: import('../types').AdminLessonCreate): Promise<import('../types').Lesson> =>
    api.post(`/api/admin/courses/${courseId}/lessons`, data).then(res => res.data),
  
  updateLesson: (lessonId: number, data: import('../types').AdminLessonUpdate): Promise<import('../types').Lesson> =>
    api.put(`/api/admin/lessons/${lessonId}`, data).then(res => res.data),
  
  deleteLesson: (lessonId: number): Promise<{ message: string }> =>
    api.delete(`/api/admin/lessons/${lessonId}`).then(res => res.data),
  
  // File management
  uploadLessonFile: (lessonId: number, file: File): Promise<import('../types').FileUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/admin/lessons/${lessonId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data);
  },
  
  deleteLessonFile: (fileId: number): Promise<{ message: string }> =>
    api.delete(`/api/admin/files/${fileId}`).then(res => res.data),

  updateFileOrder: (lessonId: number, files: Array<{ file_id: number; order: number }>): Promise<{ message: string }> =>
    api.put(`/api/admin/lessons/${lessonId}/files/order`, { files }).then(res => res.data),
  
  // Statistics
  getPlatformStats: (): Promise<import('../types').PlatformStats> =>
    api.get('/api/admin/stats').then(res => res.data),
  
  getCourseStats: (courseId: number): Promise<import('../types').CourseStats> =>
    api.get(`/api/admin/courses/${courseId}/stats`).then(res => res.data),
  
  // User management
  getInstructors: (): Promise<import('../types').User[]> =>
    api.get('/api/admin/instructors').then(res => res.data),
}; 